/**
 * Report COVER company (Monday 13312360011): the logo at the top of the cover
 * is chosen per customer (`coverCompany`), independently of the company on the
 * contract pages (`contractCompany`).
 *
 * Besides every contract company, the cover offers two brands that exist only
 * here: they have no contract template or Purchase Agreement, so they must
 * never show up in the contract picker. They live in code, not in Firebase
 * (`Misc/env/contractCompanies` is read by the contract picker too). Their
 * logo files are mapped on the web side (`coverCompanyLogos.ts`), because
 * `common/` cannot import images.
 *
 * Provisional defaults: both brands are offered in every state.
 */

export interface CoverOnlyCompany {
  longName: string
  shortName: string
  /** Contract companies that are this same brand: if one is offered, the
   * cover-only entry is skipped so the brand shows once (Jaron Oct 30: "only
   * one HOMENOW"). */
  sameAs?: readonly string[]
}

// TODO(Jaron): replace with real ROOFNOW logo (the PNG in src/web/img is a
// generated placeholder). Add a brand to this list to offer it on the cover.
export const COVER_ONLY_COMPANIES: readonly CoverOnlyCompany[] = [
  { longName: 'HomeNow', shortName: 'HOMENOW', sameAs: ['HNNV'] },
  { longName: 'RoofNow', shortName: 'ROOFNOW' },
]

type PickerCompany = {
  logo?: string
  longName?: string
  shortName?: string
}

/**
 * Options of the cover picker: the contract companies the contract picker
 * offers (already filtered), then the cover-only brands. A brand whose short
 * name (or a `sameAs` alias) a contract company already uses is skipped, so
 * no key or brand repeats.
 */
export const coverPickerCompanies = <T extends PickerCompany>(
  contractCompanies: readonly T[],
  brandLogos: Readonly<Record<string, string | undefined>>,
): Array<T | { logo: string; longName: string; shortName: string }> => {
  const taken = new Set(contractCompanies.map(({ shortName }) => shortName))
  return [
    ...contractCompanies,
    ...COVER_ONLY_COMPANIES.filter(
      ({ shortName, sameAs = [] }) =>
        !taken.has(shortName) && !sameAs.some((s) => taken.has(s)),
    ).map(({ longName, shortName }) => ({
      logo: brandLogos[shortName] || '',
      longName,
      shortName,
    })),
  ]
}
